/**
 * @Module: Ingredient Matcher
 * @Status: Ready
 * @Summary: Fuzzy ingredient matching + recipe readiness scoring
 * @Description: Shared matching logic for pantry checks, price lookups,
 *   and readiness scoring. Normalizes ingredient names, handles singular/plural,
 *   strips adjectives, and tries partial matches.
 */

import type { Recipe } from '../types/recipe';

const STRIP_WORDS = new Set([
  'fresh', 'dried', 'ground', 'diced', 'minced', 'chopped', 'sliced',
  'grated', 'shredded', 'crushed', 'frozen', 'canned', 'cooked',
  'raw', 'organic', 'large', 'small', 'medium', 'whole', 'ripe',
  'unsalted', 'salted', 'roasted', 'toasted', 'smoked', 'boneless',
  'skinless', 'extra-virgin', 'extra', 'firm', 'soft', 'plain',
  'all-purpose', 'kosher', 'sea', 'light', 'dark', 'sweet',
  'hot', 'cold', 'warm', 'red', 'green', 'white', 'yellow', 'black',
]);

/** Naive singularize: strip trailing s/es for fuzzy matching. */
export function singularize(word: string): string {
  if (word.endsWith('ies') && word.length > 4) return word.slice(0, -3) + 'y';
  if (word.endsWith('oes') && word.length > 4) return word.slice(0, -2);
  if (word.endsWith('ves') && word.length > 4) return word.slice(0, -3) + 'f';
  if (word.endsWith('ses') || word.endsWith('shes') || word.endsWith('ches') || word.endsWith('xes') || word.endsWith('zes')) {
    return word.slice(0, -2);
  }
  if (word.endsWith('s') && !word.endsWith('ss') && !word.endsWith('us') && word.length > 3) {
    return word.slice(0, -1);
  }
  return word;
}

/** Normalize an ingredient name for matching. */
export function normalizeIngredient(name: string): string {
  return name.toLowerCase().trim();
}

/** Strip adjectives/modifiers from an ingredient name. */
export function stripModifiers(name: string): string {
  const words = name.split(/\s+/);
  const stripped = words.filter(w => !STRIP_WORDS.has(w)).join(' ');
  return stripped || name;
}

/** Check if a needle ingredient matches any item in a haystack list. */
export function ingredientInList(needle: string, haystack: string[]): boolean {
  const key = normalizeIngredient(needle);
  const haystackSet = new Set(haystack.map(h => normalizeIngredient(h)));

  // 1. Exact
  if (haystackSet.has(key)) return true;

  // 2. Singular (tomatoes → tomato)
  const singular = singularize(key);
  if (singular !== key && haystackSet.has(singular)) return true;

  // 3. Plural (tomato → tomatos)
  if (haystackSet.has(key + 's')) return true;

  // 4. Strip adjectives
  const stripped = stripModifiers(key);
  if (stripped !== key) {
    if (haystackSet.has(stripped)) return true;
    if (haystackSet.has(singularize(stripped))) return true;
  }

  // 5. Last word (basmati rice → rice)
  const words = key.split(/\s+/);
  if (words.length > 1) {
    const lastWord = words[words.length - 1];
    if (haystackSet.has(lastWord) || haystackSet.has(singularize(lastWord))) return true;
  }

  // 6. Last two words (extra-virgin olive oil → olive oil)
  if (words.length > 2) {
    const lastTwo = words.slice(-2).join(' ');
    if (haystackSet.has(lastTwo) || haystackSet.has(singularize(lastTwo))) return true;
  }

  // 7. Check haystack items against needle (reverse — pantry has "flour", recipe has "all-purpose flour")
  const haystackArr = haystack.map(h => normalizeIngredient(h));
  for (let i = 0; i < haystackArr.length; i++) {
    const h = haystackArr[i];
    if (key.includes(h) || h.includes(key)) return true;
    const hSingular = singularize(h);
    if (key.includes(hSingular) || hSingular.includes(key)) return true;
  }

  return false;
}

/** Recipe readiness result. */
export interface RecipeReadiness {
  total: number;
  have: number;
  missing: string[];
  haveList: string[];
  fraction: number;
  isReady: boolean;
}

/** Calculate how ready a recipe is based on pantry staples. */
export function getRecipeReadiness(
  recipe: Recipe,
  pantryStaples: string[],
): RecipeReadiness {
  // Collect unique ingredient names from all steps
  const seen = new Map<string, string>(); // normalized → original
  for (const step of recipe.steps) {
    for (const ing of step.ingredients) {
      const key = normalizeIngredient(ing.name);
      if (!seen.has(key)) seen.set(key, ing.name);
    }
  }

  const ingredientNames: string[] = [];
  seen.forEach((original) => ingredientNames.push(original));

  const total = ingredientNames.length;
  const haveList: string[] = [];
  const missing: string[] = [];

  for (let i = 0; i < ingredientNames.length; i++) {
    const name = ingredientNames[i];
    if (ingredientInList(name, pantryStaples)) {
      haveList.push(name);
    } else {
      missing.push(name);
    }
  }

  const have = haveList.length;
  const fraction = total > 0 ? have / total : 0;

  return { total, have, missing, haveList, fraction, isReady: fraction >= 1.0 };
}
